// F4 — QA-route visibility gate (preview-only).
//
// PROBLEM this closes: `/qa-item` (PR #324) is a PUBLIC, no-auth render of any
// curriculum item's *question, answer, and options*. It exists so this repo's
// build agent can navigate a Vercel PREVIEW deploy to
// `<preview>/qa-item?id=ME0025` and visually verify the manipulative. But it
// shipped ungated, so it also renders on the PRODUCTION app — exposing the full
// answer key (`?id=ME0001…ME1278`) to anyone who guesses the path.
//
// DECISION (see docs/adr/026-qa-item-preview-only.md): allow the QA route on
// local dev and Vercel PREVIEW deploys, but NEVER on production. Two
// independent layers, belt-and-braces:
//
//   1. PRIMARY — Vercel deploy environment. Vercel sets VERCEL_ENV to
//      "production" | "preview" | "development" at build time. We surface it to
//      the client as `import.meta.env.VITE_VERCEL_ENV` (Vercel auto-exposes the
//      VITE_-prefixed system vars for Vite projects — the same mechanism the
//      Sentry release uses via VITE_VERCEL_GIT_COMMIT_SHA — and vite.config.js
//      also `define`s it explicitly so the value is baked even if that setting
//      is off). "production" => blocked; anything else (preview/development/
//      unset-local) => allowed.
//
//   2. BELT-AND-BRACES — hostname. Even if the env var is somehow misconfigured
//      to a non-"production" value on a prod build, we still refuse to render on
//      the three known production hosts.
//
// Preview deploys get hostnames like `pencilwise-<hash>-pustov.vercel.app`
// (NOT in PROD_HOSTS) with VITE_VERCEL_ENV="preview" — so QA stays available
// there. Production aliases (incl. `pencilwise.vercel.app`) are blocked by BOTH
// layers.

const PROD_HOSTS = new Set([
  "app.pencilwise.app",
  "pencilwise.app",
  "pencilwise.vercel.app",
]);

/**
 * True when QA-only routes (currently `/qa-item`) may render.
 *
 * Returns false on production (by Vercel env OR by hostname); true on local
 * dev and Vercel preview deploys.
 */
export function qaRoutesEnabled(): boolean {
  // Layer 1: Vercel deploy environment. Build-time literal on Vercel.
  const vercelEnv = import.meta.env.VITE_VERCEL_ENV;
  if (vercelEnv === "production") return false;

  // Layer 2: hostname (runtime). Guards against an env misconfig and covers
  // SSR/no-window contexts gracefully (no window => not a prod browser).
  if (typeof window !== "undefined" && window.location) {
    if (PROD_HOSTS.has(window.location.hostname)) return false;
  }

  return true;
}
